import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiFilter, FiMoreHorizontal,
  FiTrash2, FiEye, FiInbox,
  FiArrowUp, FiArrowRight, FiArrowDown, FiChevronDown, FiAlertCircle,
  FiRefreshCw, FiX, FiCalendar, FiClock, FiLayers, FiAlertTriangle, FiCheckCircle
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { normalizeRole } from '../../finance/auth/permissions';
import { fmtDate } from '../../utils/istDate';
import ConfirmModal from '../shared/ConfirmModal';
import tasksApi, { canEditTask, canChangeTaskStatus } from '../../api/tasksApi';

const STATUS_META = {
  TO_DO: { label: 'To Do', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200' },
  IN_PROGRESS: { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  COMPLETED: { label: 'Completed', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  BLOCKED: { label: 'Blocked', color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
  CREATED: { label: 'To Do', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200' },
  NOT_STARTED: { label: 'To Do', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200' },
};

const PRIORITY_META = {
  CRITICAL: { label: 'Critical', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', Icon: FiArrowUp },
  HIGH: { label: 'High', color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', Icon: FiArrowUp },
  MEDIUM: { label: 'Medium', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', Icon: FiArrowRight },
  LOW: { label: 'Low', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', Icon: FiArrowDown }
};

const AVATAR_COLORS = ['bg-indigo-500', 'bg-amber-500', 'bg-emerald-500', 'bg-blue-500', 'bg-pink-500', 'bg-purple-500', 'bg-teal-500', 'bg-orange-500'];

function Avatar({ name, size = 26 }) {
  const idx = name ? name.charCodeAt(0) % AVATAR_COLORS.length : 0;
  return (
    <div
      className={`${AVATAR_COLORS[idx]} text-white font-bold flex items-center justify-center shrink-0 rounded-full`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {name ? name.slice(0, 2).toUpperCase() : '?'}
    </div>
  );
}

function RowMenu({ task, onView, onDelete }) {
  const [open, setOpen] = useState(false);
  const myNormUser = String(localStorage.getItem('username') || '').trim().toLowerCase().split('@')[0];
  const taskCreatorNorm = String(task?.createdBy || task?.createdByName || '').trim().toLowerCase().split('@')[0];
  const isCreator = Boolean(taskCreatorNorm && myNormUser && taskCreatorNorm === myNormUser);

  if (!task.projectId && !isCreator) return null;

  return (
    <div className="relative inline-block text-left">
      <button
        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
      >
        <FiMoreHorizontal size={16} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-36 bg-white rounded-xl shadow-lg border border-slate-200 z-50 py-1">
          {task.projectId && (
            <button
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
              onClick={() => { onView(task); setOpen(false); }}
            >
              <FiEye className="text-slate-400" /> View Project
            </button>
          )}
          {task.projectId && isCreator && <div className="h-px bg-slate-100 my-1" />}
          {isCreator && (
            <button
              className="w-full text-left px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2"
              onClick={() => { onDelete(task); setOpen(false); }}
            >
              <FiTrash2 className="text-rose-400" /> Delete Task
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function TasksView() {
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';
  const normalizedRole = normalizeRole(role);
  const isAdmin = normalizedRole === 'ADMIN' || normalizedRole === 'SUPERADMIN' || normalizedRole === 'TESTER';

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'BLOCKED'
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  const [page, setPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, task: null, loading: false });
  const PER_PAGE = 25;

  const fetchTasks = useCallback(async () => {
    setLoading(true); setError('');
    try {
      let data = [];
      if (viewMode === 'all' && isAdmin) {
        data = await tasksApi.getAllTasks();
      } else {
        data = await tasksApi.getMyTasks();
      }
      setTasks(Array.isArray(data) ? data : (data?.content || []));
    } catch {
      setError('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [viewMode, isAdmin]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const fmt = (d) => fmtDate(d);

  const updateStatus = async (task, newStatus) => {
    const me = localStorage.getItem('username') || '';
    if (!canEditTask(task, me)) {
      toast.error('Only the task assignee or creator can edit this task.');
      return;
    }
    try {
      await tasksApi.updateTaskStatus(task.taskId, newStatus);
      setTasks(prev => prev.map(t => t.taskId === task.taskId ? { ...t, taskStatus: newStatus } : t));
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteTask = (task) => {
    setDeleteConfirm({ open: true, task, loading: false });
  };

  const confirmDeleteTask = async () => {
    if (!deleteConfirm.task) return;
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    try {
      await tasksApi.deleteTask(deleteConfirm.task.taskId);
      setTasks(prev => prev.filter(t => t.taskId !== deleteConfirm.task.taskId));
      toast.success('Task deleted');
      setDeleteConfirm({ open: false, task: null, loading: false });
    } catch {
      toast.error('Failed to delete task.');
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  // Helper check for overdue
  const isTaskOverdue = (task) => {
    if (!task.deadLine) return false;
    const taskStat = task.taskStatus || 'TO_DO';
    return new Date(task.deadLine) < new Date() && taskStat !== 'COMPLETED';
  };

  const searchMatched = tasks.filter(t => {
    return (t.title?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (t.username?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (t.description?.toLowerCase() || '').includes(search.toLowerCase());
  });

  // Summary counts
  const totalTasks = searchMatched.length;
  const pendingTasks = searchMatched.filter(t => {
    const s = t.taskStatus || 'TO_DO';
    return s === 'TO_DO' || s === 'CREATED' || s === 'NOT_STARTED';
  }).length;
  const inProgressTasks = searchMatched.filter(t => (t.taskStatus || '') === 'IN_PROGRESS').length;
  const completedTasks = searchMatched.filter(t => (t.taskStatus || '') === 'COMPLETED').length;
  const overdueTasks = searchMatched.filter(isTaskOverdue).length;

  const filtered = searchMatched.filter(t => {
    let taskStat = t.taskStatus || 'TO_DO';
    if (taskStat === 'CREATED' || taskStat === 'NOT_STARTED') taskStat = 'TO_DO';

    let matchStatus = true;
    if (statusFilter === 'OVERDUE') {
      matchStatus = isTaskOverdue(t);
    } else if (statusFilter !== 'ALL') {
      matchStatus = taskStat === statusFilter;
    }

    return matchStatus;
  }).sort((a, b) => (Number(b.taskId) || 0) - (Number(a.taskId) || 0));

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const allSelected = paginated.length > 0 && paginated.every(t => selected.has(t.taskId));
  const toggleAll = () => {
    if (allSelected) setSelected(p => { const n = new Set(p); paginated.forEach(t => n.delete(t.taskId)); return n; });
    else setSelected(p => { const n = new Set(p); paginated.forEach(t => n.add(t.taskId)); return n; });
  };
  const toggleRow = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-6 animate-fade-in pb-12">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              Tasks
            </h1>
            {isAdmin && (
              <div className="flex bg-slate-100 p-1 rounded-xl">
                <button
                  onClick={() => setViewMode('my')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${viewMode === 'my' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  My Tasks
                </button>
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${viewMode === 'all' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  All Tasks (Admin)
                </button>
              </div>
            )}
          </div>
          <p className="text-slate-500 text-xs font-medium mt-1">
            {tasks.length} tasks {viewMode === 'my' ? 'assigned to you' : 'across all users'}
          </p>
        </div>

        <button
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors shadow-xs text-xs font-bold"
          onClick={fetchTasks}
        >
          <FiRefreshCw className={loading ? 'animate-spin text-blue-600' : ''} /> Refresh
        </button>
      </div>

      {/* Task Summary Cards (Xpertree style) */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3.5">
        <button
          onClick={() => { setStatusFilter('ALL'); setPage(1); }}
          className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${statusFilter === 'ALL'
              ? 'bg-slate-50/80 border-slate-400 ring-2 ring-slate-400/20 shadow-sm'
              : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'
            }`}
        >
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Tasks</p>
          <p className="text-2xl font-black text-slate-800 mt-1">{totalTasks}</p>
        </button>

        <button
          onClick={() => { setStatusFilter('TO_DO'); setPage(1); }}
          className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${statusFilter === 'TO_DO'
              ? 'bg-amber-50/60 border-amber-400 ring-2 ring-amber-400/25 shadow-sm'
              : 'bg-white border-amber-200 bg-amber-50/20 hover:border-amber-300 shadow-sm'
            }`}
        >
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Pending</p>
          <p className="text-2xl font-black text-amber-800 mt-1">{pendingTasks}</p>
        </button>

        <button
          onClick={() => { setStatusFilter('IN_PROGRESS'); setPage(1); }}
          className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${statusFilter === 'IN_PROGRESS'
              ? 'bg-blue-50/60 border-blue-400 ring-2 ring-blue-400/25 shadow-sm'
              : 'bg-white border-blue-200 bg-blue-50/20 hover:border-blue-300 shadow-sm'
            }`}
        >
          <p className="text-xs font-bold uppercase tracking-wider text-blue-700">In Progress</p>
          <p className="text-2xl font-black text-blue-800 mt-1">{inProgressTasks}</p>
        </button>

        <button
          onClick={() => { setStatusFilter('COMPLETED'); setPage(1); }}
          className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${statusFilter === 'COMPLETED'
              ? 'bg-emerald-50/60 border-emerald-400 ring-2 ring-emerald-400/25 shadow-sm'
              : 'bg-white border-emerald-200 bg-emerald-50/20 hover:border-emerald-300 shadow-sm'
            }`}
        >
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Completed</p>
          <p className="text-2xl font-black text-emerald-800 mt-1">{completedTasks}</p>
        </button>

        <button
          onClick={() => { setStatusFilter('OVERDUE'); setPage(1); }}
          className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${statusFilter === 'OVERDUE'
              ? 'bg-rose-50/60 border-rose-400 ring-2 ring-rose-400/25 shadow-sm'
              : 'bg-white border-rose-200 bg-rose-50/20 hover:border-rose-300 shadow-sm'
            }`}
        >
          <p className="text-xs font-bold uppercase tracking-wider text-rose-700">Overdue</p>
          <p className="text-2xl font-black text-rose-800 mt-1">{overdueTasks}</p>
        </button>
      </div>

      {/* Main Tasks Table Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50">
          <div className="flex flex-wrap items-center gap-3 flex-1">
            {/* Search Input */}
            <div className="relative w-full sm:w-72">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-xs transition-all placeholder:text-slate-400"
                placeholder="Search tasks..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
              {search && (
                <FiX
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer hover:text-slate-600"
                  onClick={() => setSearch('')}
                />
              )}
            </div>

            {/* Quick Status Filter Tabs */}
            <div className="flex items-center p-1 bg-slate-100/80 rounded-xl overflow-x-auto max-w-full w-full sm:w-auto">
              {[
                { id: 'ALL', label: 'All Tasks' },
                { id: 'TO_DO', label: 'Pending' },
                { id: 'IN_PROGRESS', label: 'In Progress' },
                { id: 'COMPLETED', label: 'Completed' },
                { id: 'OVERDUE', label: 'Overdue' },
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => { setStatusFilter(filter.id); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${statusFilter === filter.id
                      ? 'bg-white text-slate-900 shadow-xs'
                      : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {selected.size > 0 && (
              <div className="flex items-center gap-3 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-bold rounded-xl border border-blue-100">
                <span>{selected.size} selected</span>
                <button className="hover:text-blue-900 underline" onClick={() => setSelected(new Set())}>Clear</button>
              </div>
            )}
          </div>

          <span className="text-xs text-slate-500 font-medium">Showing {filtered.length} of {tasks.length}</span>
        </div>

        {error && (
          <div className="p-4 bg-rose-50 border-b border-rose-100 text-rose-600 flex items-center gap-2 text-xs font-semibold">
            <FiAlertCircle /> {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto min-h-[380px]">
          <table className="w-full min-w-[720px] text-left text-sm text-slate-600">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-[11px] uppercase tracking-wider font-bold">
              <tr>
                <th className="px-4 py-3 w-12 text-center">
                  <button className="text-slate-400 hover:text-blue-600 transition-colors" onClick={toggleAll}>
                    <input type="checkbox" className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer h-4 w-4" checked={allSelected} onChange={toggleAll} />
                  </button>
                </th>
                <th className="px-4 py-3 min-w-[220px]">Task</th>
                <th className="px-4 py-3">Assignee</th>
                <th className="px-4 py-3">Created By</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Due Date</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={8} className="py-16 text-center text-slate-500 font-medium text-xs">Loading tasks…</td></tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-slate-200/80 text-slate-400">
                      <FiInbox size={22} />
                    </div>
                    <h3 className="text-base font-bold text-slate-800 mb-1">No tasks found</h3>
                    <p className="text-xs text-slate-500 max-w-xs mx-auto">
                      {search ? `No tasks match "${search}".` : 'Tasks matching this filter will appear here.'}
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.map((task, idx) => {
                  const sm = STATUS_META[task.taskStatus] || STATUS_META.TO_DO;
                  const priKey = PRIORITY_META[task.priority] ? task.priority : 'MEDIUM';
                  const pm = PRIORITY_META[priKey];
                  const PrioIcon = pm.Icon;
                  const isChecked = selected.has(task.taskId);
                  const overdue = isTaskOverdue(task);

                  return (
                    <tr key={task.taskId} className={`hover:bg-slate-50/80 transition-colors ${isChecked ? 'bg-blue-50/30' : (idx % 2 === 1 ? 'bg-slate-50/80' : 'bg-white')}`}>
                      <td className="px-4 py-3.5 text-center">
                        <button className="text-slate-400 hover:text-blue-600 transition-colors" onClick={() => toggleRow(task.taskId)}>
                          <input type="checkbox" className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer h-4 w-4" checked={isChecked} onChange={() => toggleRow(task.taskId)} />
                        </button>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">TASK-{task.taskId}</div>
                        <div
                          className="font-bold text-slate-800 hover:text-blue-600 cursor-pointer transition-colors text-xs"
                          onClick={() => {
                            if (task.projectId) {
                              navigate(`/ProjectDetails/${task.projectId}/task/${task.taskId}`, { state: { task, from: '/TasksView' } });
                            } else {
                              navigate(`/TasksView/${task.taskId}`, { state: { task, from: '/TasksView' } });
                            }
                          }}
                        >
                          {task.title}
                        </div>
                        {task.description && <div className="text-[11px] text-slate-500 truncate max-w-xs mt-0.5">{task.description}</div>}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-semibold text-xs text-slate-700 leading-tight line-clamp-2 break-words max-w-[105px]">{task.username || 'Unassigned'}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-xs text-slate-600 leading-tight line-clamp-2 break-words max-w-[105px]" title={task.createdBy || 'System'}>
                          {task.createdBy ? String(task.createdBy).split('@')[0] : 'System'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center justify-center gap-1 w-[88px] py-1 text-center rounded-full text-[11px] font-bold border ${pm.bg} ${pm.color} ${pm.border}`}>
                          <PrioIcon className="text-[10px]" /> {pm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        {canChangeTaskStatus(task, localStorage.getItem('username') || '', localStorage.getItem('role') || '') ? (
                          <div className="relative inline-flex items-center">
                            <select
                              className={`appearance-none w-[100px] pl-2 pr-6 py-1 text-center rounded-xl text-xs font-bold border outline-none transition-all ${sm.bg} ${sm.color} ${sm.border} cursor-pointer hover:opacity-90`}
                              value={task.taskStatus || 'TO_DO'}
                              onChange={e => updateStatus(task, e.target.value)}
                              title="Click to change status"
                            >
                              <option value="TO_DO" className="bg-white text-slate-800 font-medium">To Do</option>
                              <option value="IN_PROGRESS" className="bg-white text-slate-800 font-medium">In Progress</option>
                              <option value="COMPLETED" className="bg-white text-slate-800 font-medium">Completed</option>
                              <option value="BLOCKED" className="bg-white text-slate-800 font-medium">Blocked</option>
                            </select>
                            <FiChevronDown className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs stroke-[2.5] ${sm.color} opacity-75`} />
                          </div>
                        ) : (
                          <span className={`inline-flex items-center justify-center w-[100px] py-1 text-center rounded-xl text-xs font-bold border ${sm.bg} ${sm.color} ${sm.border}`}>
                            {sm.label}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {task.deadLine ? (
                          <div className="flex flex-col items-start gap-0.5">
                            <span className={`flex items-center gap-1 text-xs font-semibold ${overdue ? 'text-rose-600 font-bold' : 'text-slate-600'}`}>
                              <FiCalendar size={13} /> {fmt(task.deadLine)}
                            </span>
                            {overdue && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200">
                                Overdue
                              </span>
                            )}
                          </div>
                        ) : <span className="text-slate-400 text-xs">None</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        <RowMenu task={task} onView={t => navigate(`/ProjectDetails/${t.projectId}`)} onDelete={deleteTask} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 bg-slate-50/50">
            <span className="text-xs font-semibold text-slate-500">{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}</span>
            <div className="flex items-center gap-1">
              <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 text-xs disabled:opacity-50 hover:bg-slate-50" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
                return <button key={p} className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-colors ${page === p ? 'bg-blue-600 text-white border border-blue-600 shadow-xs' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} onClick={() => setPage(p)}>{p}</button>;
              })}
              <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 text-xs disabled:opacity-50 hover:bg-slate-50" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, task: null, loading: false })}
        onConfirm={confirmDeleteTask}
        title="Delete Task"
        message={deleteConfirm.task ? `Are you sure you want to delete "${deleteConfirm.task.title}"? This action cannot be undone.` : ''}
        confirmText="Delete"
        loading={deleteConfirm.loading}
      />
    </div>
  );
}